import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: lead, isLoading } = useQuery({
    queryKey: ["admin-lead", id],
    queryFn: async () => (await api.get(`/admin/leads/${id}`)).data,
  });

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      setNotes(lead.notes || "");
    }
  }, [lead]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${id}`, { status, notes });
      queryClient.invalidateQueries({ queryKey: ["admin-lead", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-leads"] });
      toast({ title: "Kaydedildi", description: "Lead bilgileri güncellendi." });
    } catch (err) {
      toast({ variant: "destructive", title: "Hata", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !lead) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-lead-detail-page">
      <button
        onClick={() => navigate("/admin/leads")}
        className="mb-4 flex items-center gap-2 text-sm text-slate-400 hover:text-white"
        data-testid="back-to-leads-button"
      >
        <ArrowLeft className="h-4 w-4" /> Leadlere dön
      </button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-white/10 bg-[#0A0A0A] lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base text-white">Lead Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <InfoRow label="Ad Soyad" value={lead.name} testId="lead-detail-name" />
            <InfoRow label="Telefon" value={lead.phone} testId="lead-detail-phone" />
            <InfoRow label="E-posta" value={lead.email || "-"} testId="lead-detail-email" />
            <InfoRow label="İşletme Türü" value={lead.business_type || "-"} testId="lead-detail-business-type" />
            <InfoRow label="Mesaj" value={lead.message || "-"} testId="lead-detail-message" />
            <InfoRow label="Kaynak" value={lead.source} testId="lead-detail-source" />
            <InfoRow label="Tarih" value={new Date(lead.created_at).toLocaleString("tr-TR")} testId="lead-detail-date" />
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-[#0A0A0A] lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base text-white">Durum ve Notlar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-slate-500">Durum</p>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-56 bg-white/5 border-white/10 text-white" data-testid="lead-detail-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Yeni</SelectItem>
                  <SelectItem value="contacted">İletişime Geçildi</SelectItem>
                  <SelectItem value="converted">Dönüştü</SelectItem>
                  <SelectItem value="lost">Kaybedildi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-slate-500">Notlar</p>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Bu lead ile ilgili notlarınızı buraya ekleyin..."
                data-testid="lead-detail-notes-textarea"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-gold text-black hover:bg-gold-light"
              data-testid="lead-detail-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-white">{value}</p>
    </div>
  );
}
